import { Box } from "@mui/material"
import { FixedNumber } from "ethers"
import React from "react"
import { useTranslation } from "react-i18next"
import AmountInput from "src/components/AmountInput"
import Button from "src/components/Button"
import { Token } from "src/constants"
import { BIG_ZERO } from "src/constants/bigNumber"
import { useIsMobile } from "src/hooks/useIsMobile"
import { usePoolTokenBalances } from "src/state/wallet/hooks"
import {
  formatNumber,
  getRawAmount,
  sanitizeStringForFN,
} from "src/utils/parseStringToBigNumber"

interface Props {
  rate: FixedNumber
  token: Token
  onDismiss: () => void
  isUnstaking: boolean
  apr: number
  inputValue: string
  onConfirm: () => void
  onInputChange: (value: string) => void
}

const InputStep = ({
  rate,
  onDismiss,
  token,
  isUnstaking,
  apr,
  inputValue,
  onConfirm,
  onInputChange,
}: Props) => {
  const { t } = useTranslation()
  const tokenBalances = usePoolTokenBalances()
  const balance = tokenBalances?.[token.symbol] || BIG_ZERO
  const isMobile = useIsMobile()

  const insufficientAmount = getRawAmount(balance)
    .subUnsafe(FixedNumber.from(sanitizeStringForFN(inputValue)))
    .isNegative()
  const disableTransaction =
    inputValue === "" ||
    FixedNumber.from(sanitizeStringForFN(inputValue)).isZero() ||
    insufficientAmount

  const converted = isUnstaking
    ? FixedNumber.from(sanitizeStringForFN(inputValue)).mulUnsafe(rate)
    : FixedNumber.from(sanitizeStringForFN(inputValue)).divUnsafe(rate)

  return (
    <Box maxWidth={480} minWidth={isMobile ? 0 : 480}>
      <AmountInput
        value={inputValue}
        onChange={onInputChange}
        tokenSymbol={token.symbol}
        label={isUnstaking ? t("Unstake") : t("Stake")}
        balance={balance}
      />
      <Box display="flex" justifyContent="space-between" mt={3}>
        <Box>{t("APR")}</Box>
        <Box>{formatNumber(apr, 2, 2)}%</Box>
      </Box>
      <Box display="flex" justifyContent="space-between" mt={1}>
        <Box>{t("Rates")}</Box>
        <Box>{`1 xFER = ${formatNumber(rate, 2, 10)} FER`}</Box>
      </Box>
      <Box display="flex" justifyContent="space-between" mt={1}>
        <Box>{t("You will receive")}</Box>
        <Box
          maxWidth={320}
          textAlign="right"
          ml={2}
          sx={{ wordBreak: "break-all" }}
        >
          {formatNumber(converted, 2, 18)} {isUnstaking ? "FER" : "xFER"}
        </Box>
      </Box>
      <Box display="flex" mt={3}>
        <Button kind="outline" onClick={onDismiss} fullWidth>
          {t("Cancel")}
        </Button>
        <Button
          fullWidth
          disabled={disableTransaction}
          onClick={onConfirm}
          ml={1}
        >
          {insufficientAmount ? t("Insufficient Balance") : t("Confirm")}
        </Button>
      </Box>
    </Box>
  )
}

export default InputStep
